/**
 * A readable line diff of one part (html, css or javascript) of a Lens custom view, for the
 * "Definition changes" panel of an askRichard proposal. A custom view runs to thousands of
 * characters, so printing it whole as Before and After hides the one line that changed; this
 * shows only the changed lines with a line of context either side.
 */
export type CustomViewDiffLine =
  | { kind: 'same' | 'removed' | 'added'; text: string; line: number }
  | { kind: 'gap'; text: string };

export type CustomViewDiff = { lines: Array<CustomViewDiffLine>; removed: number; added: number; truncated: boolean };

type Op = { kind: 'same' | 'removed' | 'added'; text: string; line: number };

/** Above this many cells the middle is shown as removed-then-added rather than aligned line by line. */
const MAX_ALIGN_CELLS = 4_000_000;

function align(before: Array<string>, after: Array<string>, beforeStart: number, afterStart: number): Array<Op> {
  const n = before.length;
  const m = after.length;
  if (!n || !m || n * m > MAX_ALIGN_CELLS) {
    return [
      ...before.map((text, i) => ({ kind: 'removed' as const, text, line: beforeStart + i })),
      ...after.map((text, j) => ({ kind: 'added' as const, text, line: afterStart + j })),
    ];
  }
  // Longest common subsequence of lines, filled from the end so the walk below goes forwards.
  const width = m + 1;
  const lcs = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * width + j] =
        before[i] === after[j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
    }
  }
  const ops: Array<Op> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      ops.push({ kind: 'same', text: after[j], line: afterStart + j });
      i++;
      j++;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
      ops.push({ kind: 'removed', text: before[i], line: beforeStart + i });
      i++;
    } else {
      ops.push({ kind: 'added', text: after[j], line: afterStart + j });
      j++;
    }
  }
  for (; i < n; i++) ops.push({ kind: 'removed', text: before[i], line: beforeStart + i });
  for (; j < m; j++) ops.push({ kind: 'added', text: after[j], line: afterStart + j });
  return ops;
}

/** Line numbers are 1-based: the line in the proposed version for kept and added lines, in the saved version for removed ones. */
export function customViewLineDiff(before: string, after: string, context = 1, maxLines = 200): CustomViewDiff {
  const a = before ? before.split('\n') : [];
  const b = after ? after.split('\n') : [];
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix++;
  let suffix = 0;
  while (
    suffix < a.length - prefix &&
    suffix < b.length - prefix &&
    a[a.length - 1 - suffix] === b[b.length - 1 - suffix]
  ) {
    suffix++;
  }
  const ops: Array<Op> = [
    ...a.slice(0, prefix).map((text, i) => ({ kind: 'same' as const, text, line: i + 1 })),
    ...align(a.slice(prefix, a.length - suffix), b.slice(prefix, b.length - suffix), prefix + 1, prefix + 1),
    ...a.slice(a.length - suffix).map((text, i) => ({ kind: 'same' as const, text, line: b.length - suffix + i + 1 })),
  ];
  const removed = ops.filter((op) => op.kind === 'removed').length;
  const added = ops.filter((op) => op.kind === 'added').length;
  const near = ops.map((op, index) =>
    ops.slice(Math.max(0, index - context), index + context + 1).some((other) => other.kind !== 'same'),
  );
  const lines: Array<CustomViewDiffLine> = [];
  ops.forEach((op, index) => {
    if (near[index]) {
      lines.push(op);
    } else if (lines.length && lines[lines.length - 1].kind !== 'gap' && near.slice(index + 1).some(Boolean)) {
      lines.push({ kind: 'gap', text: '…' });
    }
  });
  const truncated = lines.length > maxLines;
  return { lines: truncated ? lines.slice(0, maxLines) : lines, removed, added, truncated };
}
